import { useCallback, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { XIcon as CloseIcon } from 'lucide-react'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import { DropDownItem } from 'types/components'
import {
  VoucherCreateFormSchemas,
  VoucherCreateFormTypes,
} from 'schemas/campaign-voucher-schemas'
import { useVoucherCreate } from './use-voucher-create'
import {
  useVoucherMerchantsSelectQuery,
  useVoucherIssuerSelectQuery,
  useVoucherMembersSelectQuery,
} from './use-voucher-merchants-lookup'
import { getMembers } from 'api/members'
import { MemberData } from 'types/member'
import { MerchantDataTypes } from 'types/merchant'
import { useCampaignsSelectQuery } from './use-voucher-create'

import { VOUCHER_TYPES_DROPDOWN, MERCHANT_SELECTION_TYPES_DROPDOWN } from 'constants/filters'
import { getOnlyActiveMerchantAPI } from 'api/merchant'
import { toast } from 'react-toastify'

import { useQueryClient } from 'react-query'
import { QUERY_KEYS } from "constants/querykeys";
import SearchBar from 'components/SearchBar/SearchBar'
import { useVouchersStore } from 'store/vouchers'

export const CreateNewVoucherForm = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const [selectedCampaign, setSelectedCampaign] = useState<{ label: string; value: string } | null>(null);
  const [inputValue, setInputValue] = useState("");
  const { selectedMerchants, onSelectMerchants } = useVoucherMerchantsSelectQuery()
  const { selectedIssuer, onSelectIssuer } = useVoucherIssuerSelectQuery()
  const { selectedMembers, onSelectMembers } = useVoucherMembersSelectQuery()
  const { campaignOptions } = useCampaignsSelectQuery()
  const [filteredOptions, setFilteredOptions] = useState(campaignOptions)
  const [memberLimitError, setMemberLimitError] = useState<string | null>(null)

  const clonedVoucherData = useVouchersStore((state) => state.clonedVoucherData)
  // console.log("clonedVoucherData", clonedVoucherData)
  const clonedVoucherDataClear = useVouchersStore((state) => state.clonedVoucherDataClear)
  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions(campaignOptions)
    } else {
      const filtered = campaignOptions.filter(opt =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      )
      setFilteredOptions(filtered)
    }
  }, [inputValue, campaignOptions])

  const formDefaultValues: VoucherCreateFormTypes = {
    mcaId: 0,
    voucherTypeId: 0,
    voucherIssuerId: 0,
    voucherCount: null,
    merchants: null,
    pluCode: null,
    voucherName: '',
    description: '',
    imageUrl: '',
    offerId: null,
    voucherValue: 0,
    flSecurityCode: 0, //Checkbox
    productSkus: null,
    startDate: null,
    endDate: null,
  }

  const getInitialFormValues = (): VoucherCreateFormTypes => {
    if (!clonedVoucherData) {
      return formDefaultValues
    }

    // Filter merchants to remove "All stores" if mixed with specific merchants
    const filteredMerchants = clonedVoucherData.merchants?.filter(
      (m: string) => m !== 'All stores'
    ) ?? []

    const isAllStoresOnly =
      clonedVoucherData.merchants?.length === 1 && clonedVoucherData.merchants[0] === 'All stores'

    const merchantTypeId = isAllStoresOnly ? 1 : 2

    const clonedValues: VoucherCreateFormTypes = {
      ...formDefaultValues,
      voucherTypeId: clonedVoucherData.voucherTypeId,
      voucherValue: clonedVoucherData.voucherValue,
      pluCode: clonedVoucherData.pluCode ?? null,
      voucherName: clonedVoucherData.voucherName ?? '',
      startDate: null,
      endDate: null,
      description: clonedVoucherData.description ?? '',
      imageUrl: clonedVoucherData.imageUrl ?? '',
      offerId: clonedVoucherData.offerId ?? null,
      offerValue2: clonedVoucherData.offerValue2 ?? undefined,
      offerValue3: clonedVoucherData.offerValue3 ?? undefined,
      productSkus: clonedVoucherData.productSkus ?? null,
      merchants: isAllStoresOnly ? null : (filteredMerchants.length > 0 ? filteredMerchants : null),
      merchantTypeId,
      voucherIssuerId: clonedVoucherData.voucherIssuerId ?? 0,
    }
    return clonedValues
  }

  const form = useForm<VoucherCreateFormTypes>({
    resolver: zodResolver(VoucherCreateFormSchemas),
    defaultValues: getInitialFormValues(),
  })

  useEffect(() => {
    if (clonedVoucherData) {
      if (clonedVoucherData.voucherName) form.setValue('voucherName', clonedVoucherData.voucherName)
      if (clonedVoucherData.description) form.setValue('description', clonedVoucherData.description)
      if (clonedVoucherData.imageUrl) form.setValue('imageUrl', clonedVoucherData.imageUrl)
      if (clonedVoucherData.pluCode) form.setValue('pluCode', clonedVoucherData.pluCode)
      if (clonedVoucherData.productSkus) form.setValue('productSkus', clonedVoucherData.productSkus)
      if (clonedVoucherData.offerId) form.setValue('offerId', clonedVoucherData.offerId)
      if (clonedVoucherData.voucherValue) form.setValue('voucherValue', clonedVoucherData.voucherValue)
    }
  }, [clonedVoucherData, form])

  // Pre-populate mca selection
  useEffect(() => {
    if (clonedVoucherData && clonedVoucherData.mcaId) {
      const matchedCampaign = campaignOptions.find(
        (option) => Number(option.value) === clonedVoucherData.mcaId
      )
      if (matchedCampaign) {
        form.setValue('mcaId', Number(matchedCampaign.value))
      }
    }
  }, [clonedVoucherData, form, campaignOptions])

  // Pre-populate merchant selection if cloned data has merchants
  useEffect(() => {
    if (clonedVoucherData && clonedVoucherData.merchants && clonedVoucherData.merchants.length > 0) {
      const isAllStoresOnly =
        clonedVoucherData.merchants.length === 1 && clonedVoucherData.merchants[0] === 'All stores'

      const merchantTypeId = isAllStoresOnly ? 1 : 2

      form.setValue('merchantTypeId', merchantTypeId)

      if (merchantTypeId === 2) {
        // Filter out "All stores" and only keep specific merchants
        const filteredMerchants = clonedVoucherData.merchants.filter((m: string) => m !== 'All stores')
        
        const mapped = filteredMerchants.map((m: string) => {
          const match = m.match(/\((\d+)\)$/)
          const id = match ? match[1] : m
          return { label: m, value: id }
        })
        mapped.forEach(m => onSelectMerchants(m))
        form.setValue('merchants', mapped.map(m => m.value))
      }
    }
  }, [clonedVoucherData, form, onSelectMerchants])

  // Pre-populate voucher issuer selection if cloned data has voucher Issuer
  useEffect(() => {
    if (clonedVoucherData && clonedVoucherData.voucherIssuerId && clonedVoucherData.voucherIssuerId !== 0) {
      const mapped = [{ label: String(clonedVoucherData.voucherIssuerName), value: String(clonedVoucherData.voucherIssuerId) }]
      onSelectIssuer(mapped[0])
    }
  }, [clonedVoucherData, form, onSelectIssuer])

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name === "merchantTypeId" && values.merchantTypeId === 1) {
        form.setValue("merchants", null)
      }
    })
    return () => subscription.unsubscribe()
  }, [form])

  const {
    mutate: mutateVoucherCreate,
    isSuccess: isSuccessVoucherCreate,
    isError: isErrorVoucherCreate,
    isLoading: isLoadingVoucherCreate,
  } = useVoucherCreate()

  const queryClient = useQueryClient();

  const onSubmit = useCallback(
    (data: VoucherCreateFormTypes) => {
      const payload = {
        ...data,
        startDate: data.startDate ? `${data.startDate} 00:00:00` : null,
        endDate: data.endDate ? `${data.endDate} 23:59:59` : null,
      }

      // console.log('Submitting payload:', payload)

      mutateVoucherCreate(
        { formData: payload },
        {
          async onSuccess() {
            toast.success('Voucher created successfully!')
            clonedVoucherDataClear()
            onCloseModal()

            await queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === QUERY_KEYS.VOUCHER_LIST
            })
          },
          onError: () => {
            const message = JSON.parse(JSON.stringify(isErrorVoucherCreate))
            toast.error(message)
          },
        }
      )
    },
    [mutateVoucherCreate, clonedVoucherDataClear, onCloseModal, queryClient]
  )

  const [memberResults, setMemberResults] = useState<MemberData[]>([])
  const [memberSearchError, setMemberSearchError] = useState<string | null>(null)
  const [isMemberSearching, setIsMemberSearching] = useState(false)

  const [merchantResults, setMerchantResults] = useState<MerchantDataTypes[]>([])
  const [merchantSearchError, setMerchantSearchError] = useState<string | null>(null)
  const [isMerchantSearching, setIsMerchantSearching] = useState(false)

  const [issuerResults, setIssuerResults] = useState<MerchantDataTypes[]>([])
  const [issuerSearchError, setIssuerSearchError] = useState<string | null>(null)
  const [isIssuerSearching, setIsIssuerSearching] = useState(false)

  const voucherTypeId = form.watch("voucherTypeId")

  useEffect(() => {
    selectedMembers.forEach(onSelectMembers)

    form.setValue("references", [])
    setMemberLimitError(null)
    setMemberResults([])
    setMemberSearchError(null)
  }, [voucherTypeId])

  // const formValues = form.watch()
  // useEffect(() => {
  //   console.log("Current form values:", formValues)
  // }, [formValues])

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          name="mcaId"
          control={form.control}
          render={({ field }) => (
            <Dropdown
              title="Select MCA"
              options={filteredOptions}
              value={filteredOptions.find(
                (option) => Number(option.value) === field.value
              )}
              onSelect={(option: DropDownItem | null) => {
                field.onChange(option ? Number(option.value) : null)
                setSelectedCampaign(option)
              }}
              className="w-full tracking-wider"
              borderColor="#C4C4C4"
              containerClassName="focus:border-primary"
              placeholder="Search campaigns..."
              isClearable
              isSearchable
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherTypeId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              defaultValue={VOUCHER_TYPES_DROPDOWN.find(
                (i) => i.value === String(value)
              )}
              title="Voucher Type"
              options={VOUCHER_TYPES_DROPDOWN}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      {/* Show Member Lookup when voucher type is selected */}
      {form.watch("voucherTypeId") ? (
        <div className="mb-4 items-center">
          <p className="mt-4 font-light tracking-wide mb-2">
            Search members to allocate the voucher
          </p>
          <Controller
            name="references"
            control={form.control}
            render={({ field }) => {
              const voucherTypeId = form.watch("voucherTypeId")
              const maxRefs = voucherTypeId === 1 ? 1 : 10
              const canAddMore = (selectedMembers.length || 0) < maxRefs

              return (
                <>
                  <SearchBar
                    isMemberLookup
                    placeholder=""
                    startLoading={() => {
                      setIsMemberSearching(true)
                      setMemberSearchError(null)
                      setMemberResults([])
                    }}
                    stopLoading={() => setIsMemberSearching(false)}
                    startFetch={async (parsedValues) => {
                      try {
                        const members = await getMembers(
                          parsedValues.first_name || parsedValues.fname,
                          parsedValues.last_name || parsedValues.lname,
                          parsedValues.trader_id || parsedValues.id,
                          parsedValues.card_number || parsedValues.card,
                          parsedValues.email || parsedValues.email,
                          parsedValues.phone_number || parsedValues.phoneNumber
                        )
                        if (members.length === 0) {
                          setMemberSearchError("No results found")
                        } else {
                          setMemberResults(members)
                        }
                      } catch (err) {
                        setMemberSearchError("No results found")
                      } finally {
                        setIsMemberSearching(false)
                      }
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    closeSearchForm={() => { }}
                  />

                  {/* Search results */}
                  {memberSearchError && (
                    <div className="text-red-600 text-sm mt-1">{memberSearchError}</div>
                  )}
                  {isMemberSearching && (
                    <div className="text-black text-sm mt-1">Loading...</div>
                  )}
                  {memberResults.length > 0 && (
                    <div
                      className="overflow-y-auto border rounded mt-2 mb-2"
                      style={{ maxHeight: 240 }}
                    >
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Id</th>
                            <th className="px-2 py-1 text-left">First Name</th>
                            <th className="px-2 py-1 text-left">Last Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {memberResults.map((m) => {
                            const isSelected = selectedMembers.some(
                              (sel) => sel.value === String(m.traderId)
                            )

                            return (
                              <tr
                                key={m.traderId}
                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-blue-100" : ""
                                  }`}
                                onClick={() => {
                                  const newMember: DropDownItem = {
                                    value: String(m.traderId),
                                    label: `${m.traderId} ${m.firstName} ${m.lastName}`,
                                  }

                                  if (isSelected) {
                                    onSelectMembers(newMember)
                                    field.onChange(
                                      (field.value || []).filter(
                                        (id: string) => id !== newMember.value
                                      )
                                    )
                                    setMemberLimitError(null)
                                  } else {
                                    if (!canAddMore) {
                                      setMemberLimitError(
                                        `You can only select up to ${maxRefs} member${maxRefs > 1 ? "s" : ""
                                        } for this voucher type.`
                                      )
                                      return
                                    }

                                    setMemberLimitError(null)
                                    onSelectMembers(newMember)
                                    const updatedRefs =
                                      voucherTypeId === 1
                                        ? [newMember.value]
                                        : [...(field.value || []), newMember.value]

                                    field.onChange(updatedRefs)
                                  }
                                }}
                              >
                                <td className="px-2 py-1">{m.traderId}</td>
                                <td className="px-2 py-1">{m.firstName}</td>
                                <td className="px-2 py-1">{m.lastName}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Selected members */}
                  {selectedMembers.length > 0 && (
                    <div className="mb-4">
                      <p className="font-light tracking-wide">Selected Members</p>
                      <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                        {selectedMembers.map((item) => (
                          <span
                            key={item.value}
                            onClick={() => {
                              onSelectMembers(item)
                              field.onChange(
                                (field.value || []).filter(
                                  (id: string) => id !== item.value
                                )
                              )
                              setMemberLimitError(null)
                            }}
                            className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                          >
                            {item.label}
                            <CloseIcon className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reference limit message */}
                  {memberLimitError && (
                    <div className="text-red text-sm mt-1">{memberLimitError}</div>
                  )}
                </>
              )
            }}
          />
        </div>
      ) : null}

      <div className="mb-4 items-center">
        <p className="mt-4 font-light tracking-wide mb-2">
          Lookup Voucher Issuer
        </p>
        <Controller
          name="voucherIssuerId"
          control={form.control}
          render={({ field }) => (
            <>
              <SearchBar
                isMerchantLookup
                placeholder="Search By Merchant Name..."
                startLoading={() => {
                  setIsIssuerSearching(true)
                  setIssuerSearchError(null)
                  setIssuerResults([])
                }}
                stopLoading={() => setIsIssuerSearching(false)}
                startFetch={async (parsedValues) => {
                  try {
                    const merchants = await getOnlyActiveMerchantAPI(
                      parsedValues.traderId || parsedValues.id,
                      parsedValues.merchantName || parsedValues.name,
                      parsedValues.merchantCode || parsedValues.mcode
                    )
                    if (merchants.length === 0) {
                      setIssuerSearchError("No results found")
                    } else {
                      setIssuerResults(merchants)
                    }
                  } catch (err) {
                    setIssuerSearchError("No results found")
                  } finally {
                    setIsIssuerSearching(false)
                  }
                }}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                closeSearchForm={() => { }}
              />

              {issuerSearchError && (
                <div className="text-red-600 text-sm mt-1">{issuerSearchError}</div>
              )}
              {isIssuerSearching && (
                <div className="text-black text-sm mt-1">Loading...</div>
              )}
              {issuerResults.length > 0 && (
                <div
                  className="overflow-y-auto border rounded mt-2 mb-2"
                  style={{ maxHeight: 240 }}
                >
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Identifier</th>
                        <th className="px-2 py-1 text-left">Merchant Name</th>
                        <th className="px-2 py-1 text-left">Merchant Code</th>
                        <th className="px-2 py-1 text-left">External Id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issuerResults.map((m) => {
                        const isSelected = selectedIssuer.some(
                          (sel) => sel.value === String(m.traderId)
                        )

                        return (
                          <tr
                            key={m.traderId}
                            className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-100' : ''
                              }`}
                            onClick={() => {
                              const newMerchant: DropDownItem = {
                                value: String(m.traderId),
                                label: `${m.traderId} ${m.merchantName} (${m.merchantCode})`,
                              }

                              if (isSelected) {
                                onSelectIssuer(newMerchant)
                                field.onChange('')
                              } else {
                                selectedIssuer.forEach((sel) =>
                                  onSelectIssuer(sel)
                                )
                                onSelectIssuer(newMerchant)
                                field.onChange(Number(newMerchant.value))
                              }
                            }}
                          >
                            <td className="px-2 py-1">{m.traderId}</td>
                            <td className="px-2 py-1">{m.merchantName}</td>
                            <td className="px-2 py-1">{m.merchantCode}</td>
                            <td className="px-2 py-1">{m.externalId}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedIssuer.length > 0 && (
                <div className="mb-4">
                  <p className="font-light tracking-wide">Selected Merchant</p>
                  <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                    {selectedIssuer.map((item) => (
                      <span
                        key={item.value}
                        onClick={() => {
                          onSelectIssuer(item)
                          field.onChange('')
                        }}
                        className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                      >
                        {item.label}
                        <CloseIcon className="w-3 h-3" />
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="merchantTypeId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              defaultValue={MERCHANT_SELECTION_TYPES_DROPDOWN.find(
                (i) => i.value === String(value)
              )}
              title="Stores to use the voucher"
              options={MERCHANT_SELECTION_TYPES_DROPDOWN}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      {/* Show Merchant Lookup only when type 2 is selected */}
      {form.watch("merchantTypeId") === 2 && (
        <>
          <div className="mb-4 items-center">
            <p className="mt-4 font-light tracking-wide mb-2">
              Search stores to use the voucher
            </p>
            <Controller
              name="merchants"
              control={form.control}
              render={({ field }) => (
                <>
                  <SearchBar
                    isMerchantLookup
                    placeholder="Search stores..."
                    startLoading={() => {
                      setIsMerchantSearching(true)
                      setMerchantSearchError(null)
                      setMerchantResults([])
                    }}
                    stopLoading={() => setIsMerchantSearching(false)}
                    startFetch={async (parsedValues) => {
                      try {
                        const merchants = await getOnlyActiveMerchantAPI(
                          parsedValues.traderId || parsedValues.id,
                          parsedValues.merchantName || parsedValues.name,
                          parsedValues.merchantCode || parsedValues.mcode
                        )
                        if (merchants.length === 0) {
                          setMerchantSearchError("No results found")
                        } else {
                          setMerchantResults(merchants)
                        }
                      } catch (err) {
                        setMerchantSearchError("No results found")
                      } finally {
                        setIsMerchantSearching(false)
                      }
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    closeSearchForm={() => { }}
                  />

                  {/* Search results */}
                  {merchantSearchError && (
                    <div className="text-red-600 text-sm mt-1">{merchantSearchError}</div>
                  )}
                  {isMerchantSearching && (
                    <div className="text-black text-sm mt-1">Loading...</div>
                  )}
                  {merchantResults.length > 0 && (
                    <div
                      className="overflow-y-auto border rounded mt-2 mb-2"
                      style={{ maxHeight: 240 }}
                    >
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Identifier</th>
                            <th className="px-2 py-1 text-left">Merchant Name</th>
                            <th className="px-2 py-1 text-left">Merchant Code</th>
                            <th className="px-2 py-1 text-left">External Id</th>
                          </tr>
                        </thead>
                        <tbody>
                          {merchantResults.map((m) => {
                            const isSelected = selectedMerchants.some(
                              (sel) => sel.value === String(m.traderId)
                            )

                            return (
                              <tr
                                key={m.traderId}
                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-100' : ''
                                  }`}
                                onClick={() => {
                                  const newMerchant: DropDownItem = {
                                    value: String(m.traderId),
                                    label: `${m.traderId} ${m.merchantName} (${m.merchantCode})`,
                                  }

                                  if (!isSelected) {
                                    onSelectMerchants(newMerchant)
                                    field.onChange([...(field.value || []), newMerchant.value])
                                  }
                                }}
                              >
                                <td className="px-2 py-1">{m.traderId}</td>
                                <td className="px-2 py-1">{m.merchantName}</td>
                                <td className="px-2 py-1">{m.merchantCode}</td>
                                <td className="px-2 py-1">{m.externalId}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Selected merchants */}
                  {selectedMerchants.length > 0 && (
                    <div className="mb-4">
                      <p className="font-light tracking-wide">Selected Stores</p>
                      <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                        {selectedMerchants.map((item) => (
                          <span
                            key={item.value}
                            onClick={() => {
                              onSelectMerchants(item) // toggle remove
                              field.onChange(
                                (field.value || []).filter((id: string) => id !== item.value)
                              )
                            }}
                            className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                          >
                            {item.label}
                            <CloseIcon className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </>
      )}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherName"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="input"
              name="voucherName"
              label="Voucher Name"
              containerClassName="w-full"
              value={value ?? ""}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="description"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="input"
              name="description"
              label="Description"
              containerClassName="w-full"
              value={value ?? ""}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="imageUrl"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="input"
              name="imageUrl"
              label="Image URL"
              containerClassName="w-full"
              value={value ?? ""}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="offerId"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="offerId"
              label="Offer ID"
              containerClassName="flex-1"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
            />
          )}
        />
        <Controller
          control={form.control}
          name="voucherValue"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="voucherValue"
              label="Voucher Value"
              containerClassName="flex-1"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
            />
          )}
        />
        {form.formState.errors.voucherValue && (
          <span className="text-red text-sm">
            {form.formState.errors.voucherValue.message}
          </span>
        )}
      </div>
      <div className="flex items-center mb-6">
        <label htmlFor="flSecurityCode" className="text-gray-500 mr-2 font-light">
          Security Code
        </label>
        <input
          type="checkbox"
          id="flSecurityCode"
          name="flSecurityCode"
          className="mr-2"
          checked={form.watch("flSecurityCode") === 1}
          onChange={(e) => {
            form.setValue("flSecurityCode", e.target.checked ? 1 : 0)
          }}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              value={value || ""}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              value={value || ""}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="flex justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoadingVoucherCreate}
        >
          <span className="tracking-wider font-bold">
            {isLoadingVoucherCreate ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>
        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>
    </form >
  )
}
